<template>
    <main>
        <div class="top_line">
            <div class="container">
                <a :href="referrer" class="back_link" v-if="referrer">Back</a>

                <img v-if="props.logo" :src="props.logo" :alt="props.brand + ' logo'" class="brand_logo">
                <img v-if="props.brandHeader" :src="props.brandHeader" class="brand_header" :alt="props.brand">
            </div>
        </div>
        <div class="container" :data-models="props.modelsDataString" data-test-id="models_data_container">
            <h1>Finance calculator</h1>
            <div class="calculator_wrap">
                <button v-if="isMobile" class="section_toggle" :class="{ active: activeSection === 'calculator' }"
                    v-on:click="setActiveSection('calculator')">
                    <span class="toggle_number">01</span>
                    <span class="toggle_text">Calculate</span>
                </button>

                <section class="calculator_section section_calc" v-if="showCalculatorSection">
                    <div class="form_part">
                        <div class="input_wrap">
                            <span class="label_text">Model:</span>
                            <Multiselect id="model" :taggable="false" :clearable="false" :min-chars="1"
                                :searchable="true" v-model="selectedModel" label="label" :options="modelsArray"
                                data-test-id="select-model" data-cy='selectedModel' placeholder="Please select">
                            </Multiselect>
                        </div>
                    </div>

                    <div class="form_part" v-if="hasSubmodel">
                        <div class="input_wrap">
                            <span class="label_text">Sub-model:</span>
                            <Multiselect id="submodel" :taggable="false" :clearable="false" :min-chars="1"
                                :searchable="true" v-model="selectedSubmodel"
                                :options="cleanSubmodelsArray"
                                data-test-id="select-submodel" v-on:change="(e) => submodelChange(e)"
                                data-cy='selectedSubmodel'>
                                <template v-slot:singlelabel="{ value }">
                                    <div class="multiselect-single-label">
                                        <span class="multiselect-single-label-text">
                                            {{ formatCarSubmodel(value).label }} -
                                            <b>{{ getCurrency(formatCarSubmodel(value).rrp, true) }}</b>
                                        </span>
                                    </div>
                                </template>
                                <template v-slot:option="{ option }">
                                    <span class="multiselect-single-label-text">
                                        {{ formatCarSubmodel(option).label }} -
                                        <b>{{ getCurrency(formatCarSubmodel(option).rrp, true) }}</b>
                                    </span>
                                </template>
                            </Multiselect>
                        </div>
                    </div>

                    <div class="form_part">
                        <div class="input_wrap radio_selection prod_selection">
                            <span class="label_text">Finance product:</span>
                            <label v-for="(item, idx) in financeProducts" :key="idx" class="styled_radio">
                                <input type="radio" v-model="selectedProduct" :value="item.id"
                                    :data-cy="idx === 0 ? 'firstproduct' : ''"
                                    :data-firstproduct="item.id"
                                    name="selected_product" />
                                <span class="radio_text" v-html="item.title" data-test-id="select-product"></span>
                            </label>
                        </div>

                        <a :href="props.iownLink" class="help_link" target="_blank">What is iOWN?</a>
                    </div>

                    <div class="form_part">
                        <div class="input_wrap">
                            <span class="label_text">Deposit:</span>
                            <cleave v-model.lazy="deposit" :options="currencyOptions" class="styled_input" name="deposit"
                                data-test-id="input-deposit">
                            </cleave>
                            <span v-if="showDepositTip && deposit > 0" class="deposit_tip">
                                Deposit can not be higher than ${{ Math.floor(depositLimit) }}
                            </span>
                        </div>
                    </div>

                    <div class="form_part">
                        <div class="input_wrap">
                            <span class="label_text">Loan term:</span>
                            <Multiselect id="selectedTerm" :taggable="false" :clearable="false"
                                :searchable="false" data-cy="selectedTerm"
                                v-model="selectedTerm"
                                :options="props.termOptions"
                                data-test-id="select-term">
                            </Multiselect>
                        </div>
                    </div>

                    <div class="form_part">
                        <div class="input_wrap radio_selection freq_selection">
                            <span class="label_text">Repayment frequency:</span>
                            <label v-for="(item, idx) in frequencyOptions" :key="idx" class="styled_radio ">
                                <input type="radio" v-model="selectedFreq" :value="item.id" name="selectedFreq" />
                                <span class="radio_text" data-test-id="select-freq-weekly">{{ item.title }}</span>
                            </label>
                        </div>
                    </div>

                    <div class="form_part">
                        <div class="input_wrap">
                            <span class="label_text">Expected annual kilometres:
                                <VueCustomTooltip :label="props.kilometresTooltip" multiline position="is-top">
                                    <img :src="tooltipIcon" alt />
                                </VueCustomTooltip>
                            </span>
                            <Multiselect id="selectedMileage" :taggable="false" :clearable="false"
                                :searchable="false" data-cy="selectedMileage"
                                v-model="selectedMileage"
                                :options="props.mileageOptions"
                                data-test-id="select-mileage">
                            </Multiselect>
                        </div>
                    </div>

                    <div class="form_part">
                        <div class="input_wrap">
                            <span class="label_text">Estimated interest rate: {{ props.interestRate }}% p.a.</span>
                            <p class="content">
                                Our interest rates vary between 9.45% and 14.45% depending on your personal
                                circumstances.
                            </p>
                        </div>
                    </div>
                </section>

                <button v-if="isMobile" class="section_toggle" :class="{ active: activeSection === 'quote' }"
                    data-test-id="open-quote-mobile-button" v-on:click="setActiveSection('quote')">
                    <span class="toggle_number">02</span>
                    <span class="toggle_text">Your quote</span>
                </button>

                <section class="calculator_section section_quote" v-if="activeSection === 'quote' | !isMobile">
                    <span class="quote_model">{{ selectedModel }}</span>
                    <span class="quote_submodel">{{ selectedSubmodel }}</span>

                    <img v-if="selectedModelImage"
                        :src="selectedModelImage" :srcset="selectedModelImageSrcset" :alt="props.brand + ' ' + selectedModel + ' image'"
                        class="model_image" />
                    <div class="repayments">
                        <span class="repayments_numbers">
                            <span class="repayments_value">
                                <LoaderComponent v-if="calculatorRequestProcessing && !previousRepayments" />
                                <span>{{ getCurrency(repayments, false) }} </span>
                                <!-- <span><count-up :start-val="previousRepayments" :end-val="repayments" :options="counterOptions"></count-up></span> -->
                            </span>
                            <small> / {{ frequencyWord }}</small>
                        </span>

                        <span class="estimated_repayments_header">Estimated repayment</span>
                    </div>

                    <div class="quote_breakdown">
                        <div class="quote_line">
                            <span class="ql_title">Vehicle price</span>
                            <span class="ql_value">{{ getCurrency(selectedSubmodelObject.RRP, false) }}</span>
                        </div>
                        <div class="quote_line" v-if="selectedProduct === 'iown'">
                            <span class="ql_title">Guaranteed Future Value</span>
                            <span class="ql_value">
                                <LoaderComponent v-if="calculatorRequestProcessing" />
                                <span v-if="total && total.gfv">{{ getCurrency(total.gfv, false) }}</span>
                            </span>
                        </div>
                        <div class="quote_line">
                            <span class="ql_title">Total interest</span>
                            <span class="ql_value">
                                <LoaderComponent v-if="calculatorRequestProcessing" />
                                <span>{{ getCurrency(total.interest, false) }}</span>
                            </span>
                        </div>
                        <div class="quote_line">
                            <span class="ql_title">Establishment fees</span>
                            <span class="ql_value">
                                <LoaderComponent v-if="calculatorRequestProcessing" />
                                <span>{{ getCurrency(totalFees, false)}}</span>
                            </span>
                        </div>
                        <div class="quote_line">
                            <span class="ql_title">Total cost</span>
                            <span class="ql_value">
                                <LoaderComponent v-if="calculatorRequestProcessing" />
                                <span>{{ getCurrency(total.loan, false)}}</span>
                            </span>
                        </div>
                    </div>

                    <div class="quote_bottom">
                        <p class="content">
                            Note: the picture may not depict the sub-model you've chosen
                        </p>

                        <div class="controls right_aligned">
                            <button class="enquire_button" @click="navigateToContact"
                                data-test-id="contact-page-link">Enquire now</button>
                        </div>
                    </div>
                </section>
            </div>

            <div class="disclaimer" v-if="showQuoteSection">
                <h6>Disclaimer:</h6>
                <p v-html="disclaimerText" />
            </div>
        </div>
    </main>
</template>

<script>
import { defineComponent, ref, onMounted, computed, toRaw, watch, nextTick, reactive } from 'vue'
import Multiselect from '@vueform/multiselect'
import Cleave from 'vue-cleave-component'
import LoaderComponent from './LoaderComponent.vue'
import useMixin from '../mixins/commonMixin'
import imagesData from '@/data/images_data'
import CONSTANTS from '@/data/constants'
import { useRouter } from 'vue-router'

export default defineComponent({
    name: 'Calculator',
    components: {
        Multiselect,
        Cleave,
        LoaderComponent
    },
    props: {
        logo: String,
        brand: String,
        brandHeader: String,
        modelsDataString: String,
        iownLink: String,
        kilometresTooltip: String,
        clientId: String,
        interestRate: Number,
        contactUsComponent: String,
        modelsData: Array,
        termOptions: Array,
        mileageOptions: Array,
        tooltipWhite: Boolean
    },
    setup (props) {
        const
        {
            isMobile, onResize, financeProducts, currencyOptions, frequencyOptions,
            executeCalculation, depositLimit, showDepositTip, deposit, repayments,
            calculatorRequestProcessing, total, getCurrency
        } = useMixin()
        const referrer = ref('')
        const activeSection = ref('calculator')
        const selectedModel = ref('')
        const selectedSubmodel = ref('')
        const selectedSubmodelObject = ref({})
        const selectedModelSubmodels = ref([])
        const models = reactive([])
        const selectedProduct = ref({})
        const selectedTerm = ref(0)
        const initialLoad = ref(true)
        const selectedFreq = ref('')
        const selectedMileage = ref(0)
        const previousRepayments = ref(0)
        const router = useRouter()
        const totalFees = CONSTANTS.EST_FEE + CONSTANTS.PPSR_FEE
        const counterOptions = CONSTANTS.COUNTER_OPTIONS
        let timeoutId

        const showCalculatorSection = computed(() => {
            return (activeSection.value === 'calculator') | !isMobile.value
        })

        const showQuoteSection = computed(() => {
            return (activeSection.value === 'quote') | !isMobile.value
        })

        const modelsArray = computed(() => {
            return [...new Set(toRaw(props.modelsData).map(car => car.Model))]
        })

        const hasSubmodel = computed(() => {
            return !!selectedSubmodelObject.value
        })

        const frequencyWord = computed(() => {
            switch (selectedFreq.value) {
                case 'W':
                    return 'Week'
                case 'B':
                    return 'Fortnight'
                default:
                    return 'Month'
            }
        })

        const tooltipIcon = computed(() => {
            return props.tooltipWhite ? 'img/i_letter_white.svg' : 'img/i_letter.svg'
        })

        const calculationParams = computed(() => {
            return {
                model: selectedModel.value,
                submodel: selectedSubmodel.value,
                deposit: deposit.value,
                term: selectedTerm.value,
                frequency: selectedFreq.value,
                mileage: selectedMileage.value,
                repayments: repayments.value,
                rrp: selectedSubmodelObject.value.RRP
            }
        })

        const selectedModelImage = computed(() => {
            let imageData = null
            try {
                imageData = imagesData.getImage1x(
                    props.brand,
                    selectedModel.value,
                    selectedSubmodel.value
                )
            } catch {
                imageData = null
            }
            return imageData
        })

        const selectedModelImageSrcset = computed(() => {
            let imageData2x = ''
            try {
                imageData2x = imagesData.getImage2x(
                    props.brand,
                    selectedModel.value,
                    selectedSubmodel.value
                )
            } catch {
                imageData2x = ''
            }
            return `${selectedModelImage.value} 1x, ${imageData2x} 2x`
        })

        const cleanSubmodelsArray = computed(() => {
            const cleanSubmodels = selectedModelSubmodels.value.map(function (el) { return el.SubModel })
            return cleanSubmodels
        })

        const disclaimerText = computed(() => {
            return `
            Your repayments have been calculated at the estimated interest rate of ${props.interestRate}% p.a.
            However, if you apply for a loan and your application is successful,
            your interest rate and repayment amount may be different to those shown above.
            An establishment fee of $${CONSTANTS.EST_FEE} and $${CONSTANTS.PPSR_FEE} PPSR fee applies to
            each loan, the repayment amount shown above includes repayment of that.
            The repayments have been calculated on the base standard retail purchase
            price of the ${props.brand} model selected and excludes on road cost.
            We endeavour to ensure that all information on this website is accurate at the time of upload to the website.
            However product features, models, illustrations, representations, specifications and prices
            are subject to change without notice. For more information please contact your local ${props.brand} dealer.
            `
        })

        const navigateToContact = () => {
            window.passedData = calculationParams.value
            window.scrollTo({ top: 0, behavior: 'smooth' })
            router.push({ name: props.contactUsComponent })
        }

        const setActiveSection = (attr1) => {
            activeSection.value = attr1
            if (isMobile.value) {
                window.scrollTo({ top: 200, behavior: 'smooth' })
            }
        }

        const selectSubmodels = (attr1) => {
            if (!attr1 || models.value.indexOf(attr1) < 0) {
                return []
            }
            return toRaw(props.modelsData).filter((el) => el.Model === attr1)
        }

        const submodelChange = async (e) => {
            if (e) {
                selectedSubmodel.value = e
                const newSelectedSubmodelObject =
                    selectedModelSubmodels.value.find((el) => el.SubModel === e)
                selectedSubmodelObject.value = toRaw(newSelectedSubmodelObject)
                await nextTick()
                callExecuteCalculation()
            } else {
                submodelChange(selectedModelSubmodels.value[0].SubModel)
            }
        }

        const callExecuteCalculation = async () => {
            if (selectedSubmodelObject.value.Code) {
                try {
                    await executeCalculation({
                        rrp: selectedSubmodelObject.value.RRP,
                        code: selectedSubmodelObject.value.Code,
                        selectedFreq: selectedFreq.value,
                        selectedProduct: selectedProduct.value,
                        selectedTerm: selectedTerm.value,
                        selectedMileage: selectedMileage.value,
                        clientId: props.clientId,
                        interestRate: props.interestRate
                    })
                } catch (error) {
                    console.error('Error occurred during calculation:', error)
                }
            }
        }

        function formatCarSubmodel (value) {
            const selectedCar = toRaw(props.modelsData).find(function (el) {
                return el.Model === selectedModel.value && el.SubModel === toRaw(value).label
            })
            return selectedCar && selectedCar.SubModel
                ? { label: selectedCar.SubModel, rrp: selectedCar.RRP }
                : {}
        }

        watch(selectedModel, async (newModel, oldModel) => {
            if (!newModel) {
                selectedModel.value = oldModel
            } else {
                selectedModelSubmodels.value = selectSubmodels(newModel)
                await nextTick()
                selectedSubmodel.value = selectedModelSubmodels.value[0].SubModel
            }
        })

        watch(selectedTerm, (newData) => {
            if (newData && !initialLoad.value) {
                callExecuteCalculation()
            }
        })

        watch(selectedFreq, (newData) => {
            if (newData && !initialLoad.value) {
                callExecuteCalculation()
            }
        })

        watch(selectedMileage, (newData) => {
            if (newData && !initialLoad.value) {
                callExecuteCalculation()
            }
        })

        watch(repayments, (newData, oldData) => {
            previousRepayments.value = oldData
        })

        watch(selectedProduct, (newData) => {
            callExecuteCalculation()
        })

        watch(deposit, (newData) => {
            if (!initialLoad.value) {
                clearTimeout(timeoutId)
                timeoutId = setTimeout(() => {
                    if (newData > depositLimit.value) {
                        deposit.value = Math.floor(depositLimit.value)
                        showDepositTip.value = true
                    } else if (newData < Math.floor(depositLimit.value)) {
                        showDepositTip.value = false
                        callExecuteCalculation()
                    } else {
                        callExecuteCalculation()
                    }
                }, 500)
            }
        })

        const loadModels = () => {
            models.value = toRaw(props.modelsData).length ? toRaw(props.modelsData).map((el) => el.Model) : []
            selectedModel.value = models.value[0]
            selectedModelSubmodels.value =
                selectSubmodels(selectedModel.value)
            initialLoad.value = false
        }

        const init = () => {
            onResize()
            window.addEventListener('resize', onResize, { passive: true })
            if (document.referrer &&
                document.referrer.indexOf(location.hostname) === -1 &&
                document.referrer.indexOf('teams') < 0 &&
                document.referrer.indexOf('visualstudio') < 0) {
                referrer.value = document.referrer
            }

            selectedProduct.value = financeProducts[0].id
            selectedTerm.value = props.termOptions[1].months
            selectedFreq.value = frequencyOptions[0].id
            selectedMileage.value = props.mileageOptions[0].kms

            setTimeout(() => loadModels(), 500)
        }

        onMounted(() => {
            init()
        })

        return {
            props,
            referrer,
            isMobile,
            setActiveSection,
            activeSection,
            showCalculatorSection,
            showQuoteSection,
            modelsArray,
            selectedModel,
            selectedSubmodel,
            selectedSubmodelObject,
            hasSubmodel,
            selectedModelSubmodels,
            submodelChange,
            formatCarSubmodel,
            getCurrency,
            financeProducts,
            selectedProduct,
            currencyOptions,
            deposit,
            depositLimit,
            showDepositTip,
            selectedTerm,
            frequencyOptions,
            selectedFreq,
            frequencyWord,
            selectedMileage,
            selectedModelImage,
            selectedModelImageSrcset,
            calculatorRequestProcessing,
            callExecuteCalculation,
            cleanSubmodelsArray,
            repayments,
            previousRepayments,
            counterOptions,
            total,
            totalFees,
            navigateToContact,
            disclaimerText,
            tooltipIcon
        }
    }
})
</script>
